import React, { useState, useEffect } from "react";
import {
    Box,
    Typography,
    Button,
    Card,
    Paper,
    Divider,
    IconButton,
    Tooltip,
    CircularProgress,
    Alert,
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Checkbox,
    Grid,
} from "@mui/material";
import {
    Edit as EditIcon,
    Delete as DeleteIcon,
    Add as AddIcon,
} from "@mui/icons-material";
import axios from "axios";
import TopBar from "../globalComponents/topbar";
import Footer from "../globalComponents/footer";

const MentorAvailability = () => {
    const [selectedDate, setSelectedDate] = useState("");
    const [timeSlots, setTimeSlots] = useState([{ start: "09:00", end: "10:00" }]);
    const [savedAvailability, setSavedAvailability] = useState([]);
    
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");

    const user = JSON.parse(localStorage.getItem("user"));
    const mentorId = user?.id || user?._id;

    useEffect(() => {
        fetchSavedAvailability();
        // Set today's date as default
        setSelectedDate(new Date().toISOString().split("T")[0]);
    }, []);

    // Fetch saved availability
    const fetchSavedAvailability = async () => {
        try {
            setLoading(true);
            const res = await axios.get(
                `https://backend-global-1vq9.onrender.com/api/mentorsAvail/${mentorId}/availability`
            );
            
            if (res.data && res.data.data) {
                setSavedAvailability(res.data.data);
            }
        } catch (err) {
            console.error("Failed to fetch availability:", err);
        } finally {
            setLoading(false);
        }
    };

    // Add new time slot
    const addTimeSlot = () => {
        setTimeSlots([...timeSlots, { start: "09:00", end: "10:00" }]);
    };

    // Remove time slot
    const removeTimeSlot = (index) => {
        if (timeSlots.length > 1) {
            setTimeSlots(timeSlots.filter((_, i) => i !== index));
        }
    };

    // Update time slot
    const updateTimeSlot = (index, field, value) => {
        const updatedSlots = [...timeSlots];
        updatedSlots[index][field] = value;
        setTimeSlots(updatedSlots);
    };

    // Save availability to backend
    const saveAvailability = async () => {
        if (!selectedDate) {
            setError("Please select a date");
            return;
        }

        // Validate time slots
        for (let slot of timeSlots) {
            if (!slot.start || !slot.end) {
                setError("Please fill in all time slots");
                return;
            }
            if (slot.start >= slot.end) {
                setError("Start time must be before end time");
                return;
            }
        }

        setLoading(true);
        setError("");
        setSuccess("");

        try {
            const payload = {
                date: selectedDate,
                timeSlots: timeSlots
            };

            await axios.post(
                `https://backend-global-1vq9.onrender.com/api/mentorsAvail/${mentorId}/availability`,
                payload
            );

            setSuccess("Availability saved successfully!");
            fetchSavedAvailability();
            
            // Reset form
            setTimeSlots([{ start: "09:00", end: "10:00" }]);
            setSelectedDate(new Date().toISOString().split("T")[0]);
        } catch (err) {
            setError(err.response?.data?.message || "Failed to save availability");
        } finally {
            setLoading(false);
        }
    };

    // Delete saved availability
    const deleteAvailability = async (availId) => {
        const confirmDelete = window.confirm("Are you sure you want to delete this availability?");
        if (!confirmDelete) return;

        try {
            await axios.delete(
                `https://backend-global-1vq9.onrender.com/api/mentorsAvail/${mentorId}/${availId}`
            );
            
            setSavedAvailability(prev => prev.filter(slot => slot._id !== availId));
            setSuccess("Availability deleted!");
        } catch (err) {
            setError("Failed to delete availability");
        }
    };

    // Load existing availability for editing
    const loadForEdit = (availability) => {
        setSelectedDate(availability.date);
        setTimeSlots(availability.timeSlots.map(slot => ({
            start: slot.start,
            end: slot.end
        })));
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    // Format date for display
    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', { 
            weekday: 'long', 
            year: 'numeric', 
            month: 'long', 
            day: 'numeric' 
        });
    };

    // Format time for display
    const formatTime = (time) => {
        const [hours, minutes] = time.split(":");
        const hour = parseInt(hours);
        const ampm = hour >= 12 ? "PM" : "AM";
        const displayHour = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
        return `${displayHour}:${minutes} ${ampm}`;
    };

    return (
        <Box sx={{ bgcolor: "#f8fafc", minHeight: "100vh" }}>
            <TopBar />
            
            <Box sx={{ p: 5, maxWidth: 1400, mx: "auto" }}>
                <Typography variant="h4" fontWeight="bold" textAlign="center" mb={4}>
                    Availability Settings
                </Typography>

                {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>{error}</Alert>}
                {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess("")}>{success}</Alert>}

                <Grid container spacing={3}>
                    {/* LEFT SECTION - Set Your Availability */}
                    <Grid item style={{width:"55%"}} md={8}>
                        <Card sx={{ p: 3, borderRadius: 3, boxShadow: 3 }}>
                            <Typography variant="h6" fontWeight="bold" mb={2}>
                                Set Your Availability
                            </Typography>
                            <Typography variant="body2" color="text.secondary" mb={3}>
                                Choose a date and add time slots when you're available for sessions with mentees
                            </Typography>

                            {/* Date Picker */}
                            <Box sx={{ mb: 3 }}>
                                <Typography variant="subtitle2" fontWeight="bold" mb={1}>
                                    Select Date
                                </Typography>
                                <TextField
                                    type="date"
                                    value={selectedDate}
                                    onChange={(e) => setSelectedDate(e.target.value)}
                                    fullWidth
                                    size="small"
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Box>

                            <Divider sx={{ my: 3 }} />

                            {/* Time Slots Section */}
                            <Box sx={{ mb: 3 }}>
                                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                                    <Typography variant="subtitle2" fontWeight="bold">
                                        Time Slots
                                    </Typography>
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        startIcon={<AddIcon />}
                                        onClick={addTimeSlot}
                                        sx={{ 
                                            textTransform: "none",
                                            borderColor: "#00AEEF",
                                            color: "#00AEEF",
                                            "&:hover": {
                                                borderColor: "#0099DD",
                                                bgcolor: "#E6F7FD"
                                            }
                                        }}
                                    >
                                        Add Time Slot
                                    </Button>
                                </Box>

                                {timeSlots.map((slot, index) => (
                                    <Box 
                                        key={index} 
                                        sx={{ 
                                            display: "flex", 
                                            gap: 2, 
                                            mb: 2,
                                            alignItems: "center",
                                            p: 2,
                                            bgcolor: "#f9fafb",
                                            borderRadius: 2,
                                            border: "1px solid #e5e7eb"
                                        }}
                                    >
                                        <Typography sx={{ fontWeight: "bold", color: "#666", minWidth: 30 }}>
                                            {index + 1}.
                                        </Typography>
                                        <TextField
                                            label="Start Time"
                                            type="time"
                                            value={slot.start}
                                            onChange={(e) => updateTimeSlot(index, "start", e.target.value)}
                                            size="small"
                                            sx={{ flex: 1 }}
                                            InputLabelProps={{ shrink: true }}
                                        />
                                        <Typography sx={{ color: "#666" }}>to</Typography>
                                        <TextField
                                            label="End Time"
                                            type="time"
                                            value={slot.end}
                                            onChange={(e) => updateTimeSlot(index, "end", e.target.value)}
                                            size="small"
                                            sx={{ flex: 1 }}
                                            InputLabelProps={{ shrink: true }}
                                        />
                                        {timeSlots.length > 1 && (
                                            <IconButton 
                                                size="small" 
                                                color="error"
                                                onClick={() => removeTimeSlot(index)}
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        )}
                                    </Box>
                                ))}
                            </Box>

                            <Divider sx={{ my: 3 }} />

                            {/* Summary */}
                            <Box sx={{ mb: 3, p: 2, bgcolor: "#E6F7FD", borderRadius: 2 }}>
                                <Typography variant="subtitle2" fontWeight="bold" color="#00AEEF" mb={1}>
                                    Summary
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Date: {selectedDate ? formatDate(selectedDate) : "Not selected"}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Time Slots: {timeSlots.length}
                                </Typography>
                            </Box>

                            <Button
                                variant="contained"
                                fullWidth
                                onClick={saveAvailability}
                                disabled={loading}
                                sx={{
                                    background: "linear-gradient(180deg, #6BD0F6, #00AEEF)",
                                    textTransform: "none",
                                    fontWeight: "bold",
                                    py: 1.5,
                                    "&:hover": {
                                        background: "linear-gradient(180deg, #5AC0E6, #0099DD)",
                                    }
                                }}
                            >
                                {loading ? <CircularProgress size={24} sx={{ color: "white" }} /> : "Save Availability"}
                            </Button>
                        </Card>
                    </Grid>

                    {/* RIGHT SECTION - Saved Availabilities */}
                    <Grid item style={{width:"40%"}} md={6}>
                        <Card sx={{ p: 3, borderRadius: 3, boxShadow: 3, height: "fit-content" }}>
                            <Typography variant="h6" fontWeight="bold" mb={2}>
                                Saved Availabilities
                            </Typography>

                            {loading && savedAvailability.length === 0 ? (
                                <Box display="flex" justifyContent="center" py={3}>
                                    <CircularProgress size={30} />
                                </Box>
                            ) : savedAvailability.length === 0 ? (
                                <Box sx={{ textAlign: "center", py: 4 }}>
                                    <Typography variant="body2" color="text.secondary">
                                        No saved availabilities yet
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        Set your availability to get started
                                    </Typography>
                                </Box>
                            ) : (
                                <Box sx={{ display: "flex", flexDirection: "column", gap: 2, maxHeight: "70vh", overflowY: "auto" }}>
                                    {savedAvailability.map((avail) => (
                                        <Paper
                                            key={avail._id}
                                            sx={{
                                                p: 2,
                                                border: "1px solid #e0e0e0",
                                                borderRadius: 2,
                                                transition: "all 0.2s",
                                                "&:hover": {
                                                    boxShadow: 2,
                                                    borderColor: "#00AEEF"
                                                }
                                            }}
                                        >
                                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 1 }}>
                                                <Box sx={{ flex: 1 }}>
                                                    <Typography variant="body2" fontWeight="bold" color="primary">
                                                        {formatDate(avail.date)}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {avail.timeSlots.length} time slot{avail.timeSlots.length !== 1 ? 's' : ''}
                                                    </Typography>
                                                </Box>
                                                <Box>
                                                    <Tooltip title="Edit">
                                                        <IconButton 
                                                            size="small" 
                                                            color="primary"
                                                            onClick={() => loadForEdit(avail)}
                                                        >
                                                            <EditIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="Delete">
                                                        <IconButton 
                                                            size="small" 
                                                            color="error"
                                                            onClick={() => deleteAvailability(avail._id)}
                                                        >
                                                            <DeleteIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Box>
                                            </Box>

                                            <Divider sx={{ my: 1 }} />

                                            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                                                {avail.timeSlots.map((slot, idx) => (
                                                    <Box 
                                                        key={slot._id} 
                                                        sx={{ 
                                                            display: "flex", 
                                                            alignItems: "center",
                                                            gap: 1,
                                                            p: 1,
                                                            bgcolor: "#f9fafb",
                                                            borderRadius: 1
                                                        }}
                                                    >
                                                        <Box
                                                            sx={{
                                                                width: 6,
                                                                height: 6,
                                                                borderRadius: "50%",
                                                                bgcolor: "#00AEEF"
                                                            }}
                                                        />
                                                        <Typography variant="caption" fontWeight="500">
                                                            {formatTime(slot.start)} - {formatTime(slot.end)}
                                                        </Typography>
                                                    </Box>
                                                ))}
                                            </Box>

                                            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                                                Created: {new Date(avail.createdAt).toLocaleDateString()}
                                            </Typography>
                                        </Paper>
                                    ))}
                                </Box>
                            )}
                        </Card>
                    </Grid>
                </Grid>
            </Box>

            <Footer />
        </Box>
    );
};

export default MentorAvailability;
